import { useState } from 'react';
import clsx from 'clsx';
import {
  AppBar,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  Menu,
  Home,
  Settings as SettingsIcon,
  History,
  AccountBox,
  Person,
  AssignmentTurnedIn,
  Assignment,
  Computer,
  Business,
  PeopleAlt,
  DynamicFeed,
  Link as LinkIcon,
  LocalOffer,
  Paid,
} from '@mui/icons-material';
import { Switch, Route, useLocation } from 'react-router-dom';
import Logout from '../auth/Logout';
import Dashboard from '../dashboard/Dashboard';
import useStyles from './layoutStyles';
import Rma from '../rma/Rma';
import Support from '../support/Support';
import Settings from '../settings/Settings';
import Footer from './Footer';
import Hardware from '../hardware/Hardware';
import InventoryHardwares from '../inventory/InventoryHardware';
import InventoryParts from '../inventory/InventoryParts';
import SoftwareSold from '../software-sold/SoftwareSold';
import Companies from '../companies/Companies';
import LptRma from '../lpt-rma/LptRma';
import LptRmaHistory from '../lpt-rma-history/LptRmaHistory';
import Login from '../auth/Login';
import PrivateRoute from '../../components/PrivateRoute';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../auth/role.enum';
import PasswordReset from '../auth/PasswordReset';
import ForgotPassword from '../auth/ForgotPassword';
import { isExcludedFromMenuBar } from './layoutUtils';
import RequestAccess from '../auth/RequestAccess';
import Signups from '../users/Signups';
import Users from '../users/Users';
import Demos from '../demos/Demos';
import SoftwareDisplay from '../software-display/SoftwareDisplay';
import SoftwareVersions from '../versions/SoftwareVersions';
import SoftwareLinks from '../software-links/SoftwareLinks';
import AnalyzerLinks from '../analyzer-links/AnalyzerLinks';
import Firmware from '../firmware/Firmware';
import Utilities from '../utilities/Utilities';
import HardwareDisplay from '../hardware-display/HardwareDisplay';
import Orders from '../orders/Orders';
import LptCompanyPage from '../company-display/LptCompanyPage';
import SupportPlans from '../support-plans/SupportPlans';

const Layout = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const { pathname } = useLocation();
  return (
    <>
      {!isExcludedFromMenuBar(pathname) && (
        <div className={classes.root}>
          <AppBar
            className={clsx(classes.appBar, {
              [classes.appBarShift]: open,
            })}
            position='static'
          >
            <Toolbar>
              <IconButton
                edge='start'
                color='inherit'
                className={clsx(classes.menuButton, open && classes.hide)}
                onClick={() => handleDrawerOpen()}
                size='large'
              >
                <Menu />
              </IconButton>
              <img
                src={process.env.PUBLIC_URL + '/LPTIconSmall.webp'}
                height='50px'
                alt='LPT logo'
              />
              <Typography
                component={Link}
                to='/'
                align='left'
                className={classes.title}
                color='inherit'
                style={{ marginLeft: '1rem' }}
              >
                {user ? `Welcome, ${user.name}` : 'Logged In'}
              </Typography>
              <Button component={Link} to='/logout' color='inherit'>
                Logout
              </Button>
            </Toolbar>
          </AppBar>
          <Drawer
            className={classes.drawer}
            variant='persistent'
            anchor='left'
            open={open}
            classes={{ paper: classes.drawerPaper }}
          >
            <div className={classes.drawerHeader}>
              <IconButton onClick={() => handleDrawerClose()} size='large'>
                <ChevronLeft />
              </IconButton>
            </div>
            <Divider />
            <List>
              <ListItem
                className={clsx(pathname === '/' && classes.active)}
                component={Link}
                to='/'
                button
              >
                <Home className={classes.menuButton} />
                <Typography variant='subtitle1'>Home</Typography>
              </ListItem>
              <ListItem
                className={clsx(pathname === '/rma' && classes.active)}
                component={Link}
                to='/rma'
                button
              >
                <AccountBox className={classes.menuButton} />
                <Typography variant='subtitle1'>RMA</Typography>
              </ListItem>
              <ListItem
                className={clsx(pathname === '/settings' && classes.active)}
                component={Link}
                to='/settings'
                button
              >
                <SettingsIcon className={classes.menuButton} />
                <Typography variant='subtitle1'>User Settings</Typography>
              </ListItem>
              <ListItem
                className={clsx(pathname === '/support' && classes.active)}
                component={Link}
                to='/support'
                button
              >
                <Person className={classes.menuButton} />
                <Typography variant='subtitle1'>Support</Typography>
              </ListItem>
              {user && user.role === Role.Admin && (
                <>
                  <Divider />
                  <ListItem className={classes.active}>
                    <Typography style={{ color: '#fff' }}>LPT Admin</Typography>
                  </ListItem>
                  <Divider />
                  <ListItem
                    className={clsx(
                      (pathname === '/inventory/analyzers' ||
                        pathname === '/inventory/parts') &&
                        classes.active
                    )}
                    component={Link}
                    to='/inventory/analyzers'
                    button
                  >
                    <Assignment className={classes.menuButton} />
                    <Typography variant='subtitle1'>Inventory</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(
                      pathname === '/hardware-sold' && classes.active
                    )}
                    component={Link}
                    to='/hardware-sold'
                    button
                  >
                    <AssignmentTurnedIn className={classes.menuButton} />
                    <Typography variant='subtitle1'>Hardware Sold</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(
                      pathname === '/softwaresold' && classes.active
                    )}
                    component={Link}
                    to='/softwaresold'
                    button
                  >
                    <Computer className={classes.menuButton} />
                    <Typography variant='subtitle1'>Software Sold</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(pathname === '/orders' && classes.active)}
                    component={Link}
                    to='/orders'
                    button
                  >
                    <LocalOffer className={classes.menuButton} />
                    <Typography variant='subtitle1'>Orders</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(pathname === '/lpt-rma' && classes.active)}
                    component={Link}
                    to='/lpt-rma'
                    button
                  >
                    <AccountBox className={classes.menuButton} />
                    <Typography variant='subtitle1'>RMA</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(
                      pathname === '/lpt-rma-history' && classes.active
                    )}
                    component={Link}
                    to='/lpt-rma-history'
                    button
                  >
                    <History className={classes.menuButton} />
                    <Typography variant='subtitle1'>RMA History</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(
                      pathname === '/companies' && classes.active
                    )}
                    component={Link}
                    to='/companies'
                    button
                  >
                    <Business className={classes.menuButton} />
                    <Typography
                      variant='subtitle1'
                      style={{ fontSize: '.99rem' }}
                    >
                      Company Management
                    </Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(
                      (pathname === '/signups' || pathname === '/users') &&
                        classes.active
                    )}
                    component={Link}
                    to='/users'
                    button
                  >
                    <PeopleAlt className={classes.menuButton} />
                    <Typography variant='subtitle1'>User Management</Typography>
                  </ListItem>
                  <ListItem
                    className={clsx(pathname === '/demos' && classes.active)}
                    component={Link}
                    to='/demos'
                    button
                  >
                    <DynamicFeed className={classes.menuButton} />
                    <Typography
                      variant='subtitle1'
                      style={{ fontSize: '.99rem' }}
                    >
                      Demos
                    </Typography>
                  </ListItem>{' '}
                  <ListItem
                    className={clsx(
                      pathname === '/support-plans' && classes.active
                    )}
                    component={Link}
                    to='/support-plans'
                    button
                  >
                    <Paid className={classes.menuButton} />
                    <Typography
                      variant='subtitle1'
                      style={{ fontSize: '.99rem' }}
                    >
                      Support Plans
                    </Typography>
                  </ListItem>{' '}
                  <ListItem
                    className={clsx(
                      (pathname === '/links' ||
                        pathname === '/firmware' ||
                        pathname === '/analyzer-type-links' ||
                        pathname === '/software-links' ||
                        pathname === '/utilities') &&
                        classes.active
                    )}
                    component={Link}
                    to='/software-links'
                    button
                  >
                    <LinkIcon className={classes.menuButton} />
                    <Typography
                      variant='subtitle1'
                      style={{ fontSize: '.99rem' }}
                    >
                      Link Management
                    </Typography>
                  </ListItem>
                </>
              )}
            </List>
          </Drawer>
        </div>
      )}
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open && !isExcludedFromMenuBar(pathname),
        })}
      >
        <Switch>
          <Route path='/login' component={Login} />
          <Route path='/logout' component={Logout} />
          <Route path='/password-reset' component={PasswordReset} />
          <Route path='/forgot-password' component={ForgotPassword} />
          <Route path='/request-access' component={RequestAccess} />

          <PrivateRoute path='/rma' component={Rma} />
          <PrivateRoute path='/settings' component={Settings} />
          <PrivateRoute path='/support' component={Support} />
          <PrivateRoute
            path='/inventory/hardware'
            component={InventoryHardwares}
          />
          <PrivateRoute path='/inventory/parts' component={InventoryParts} />
          <PrivateRoute path='/lpt-rma' component={LptRma} />
          <PrivateRoute path='/lpt-rma-history' component={LptRmaHistory} />
          <PrivateRoute path='/hardware-sold' component={Hardware} />
          <PrivateRoute path='/softwaresold' component={SoftwareSold} />
          <PrivateRoute
            path='/inventory/analyzers'
            component={InventoryHardwares}
          />
          <PrivateRoute path='/inventory/parts' component={InventoryParts} />
          <PrivateRoute path='/companies/details' component={LptCompanyPage} />
          <PrivateRoute path='/companies' component={Companies} />
          <PrivateRoute path='/signups' component={Signups} />
          <PrivateRoute path='/users' component={Users} />
          <PrivateRoute path='/demos' component={Demos} />
          <PrivateRoute path='/software' component={SoftwareDisplay} />
          <PrivateRoute path='/hardware' component={HardwareDisplay} />
          <PrivateRoute path='/links' component={SoftwareVersions} />
          <PrivateRoute path='/orders' component={Orders} />
          <PrivateRoute path='/software-links' component={SoftwareLinks} />
          <PrivateRoute path='/support-plans' component={SupportPlans} />
          <PrivateRoute path='/analyzer-type-links' component={AnalyzerLinks} />
          <PrivateRoute path='/firmware' component={Firmware} />
          <PrivateRoute path='/utilities' component={Utilities} />
          <PrivateRoute path='/' component={Dashboard} />
        </Switch>
        <Footer />
      </main>
    </>
  );
};

export default Layout;
